import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Table,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightOutlined,
  LoadingOutlined,
  DeleteOutlined,
  EditOutlined
} from "@ant-design/icons";
import CompanyServices from "../../services/CompanyServices";
import { translate } from "../../locale";
import { TableHelper } from "../../helpers/TableHelper";
import { AdminContext } from "../../providers/AdminContext";
import AddCompany from "../Modals/AddCompany";
import EditCompany from "../Modals/EditCompany";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const CompaniesTable = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
    searchText: "",
  });
  const [total, setTotal] = useState(0);
  const { user } = useContext(AdminContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null)

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (text, record, index) => (
        <div className="flex space-x-2">
          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            size="small"
            className="border-0"
            onClick={() => navigate(`/admin/companies/${record.id}`)}
          />
          <Popconfirm
            title="¿Estás seguro de eliminar el registro？"
            okText="Si"
            cancelText="No"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Eliminar" color="blur">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            className="border-0"
            onClick={() => setShowEditModal(record.id)}
          />
        </div>
      ),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <a
          onClick={() => navigate(`/admin/companies/${record.id}`)}
          className="text-black hover:text-custom_primary"
        >
          {record.name}
        </a>
      ),
      sorter: { multiple: 1 },
    },
  ];

  useEffect(() => {
    if (user !== null) {
      setIsAdmin(user.user.rol === "eira-super-admin");
    }
  }, [user]);

  useEffect(() => {
    loadCompanies();
  }, [JSON.stringify(tableParams), showEditModal]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      let data = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        data = { ...data, order_by: sorter };
      }
      if (tableParams.searchText !== "") {
        data = { ...data, name: tableParams.searchText };
      }
      const res = await CompanyServices.getCompanies(data);

      if (
        res.status >= 200 &&
        res.status < 300 &&
        res.data.enterprises.length > 0
      ) {
        setData(res.data.enterprises);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const res = await CompanyServices.deleteCompany(id);
      if (res.status >= 200 && res.status < 300) {
        message.success(
          translate("La {0} se elimino exitosamente.", translate("empresa"))
        );
        loadCompanies();
      } else {
        message.error(res.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };
  const handleModalCancel = () => {
    setModalVisible(false);
  };

  const handleModalComplete = () => {
    setModalVisible(false);
    loadCompanies();
  };

  const handleAddCompany = () => {
    setModalVisible(true);
  };
  const searchData = (value) => {
    setTableParams((v) => ({
      pagination: {
        ...v.pagination,
        current: 1,
      },
      searchText: value,
    }));
  };

  return (
    <>
      <Row gutter={16}>
        <Col className="gutter-row" span="18">
          {isAdmin && (
            <Button
              type="primary"
              className="bg-custom_primary border-custom_primary"
              onClick={handleAddCompany}
            >
              {translate("Agregar empresa")}
            </Button>
          )}
          <AddCompany
            protocolId={0}
            visible={modalVisible}
            onCancel={handleModalCancel}
            onComplete={handleModalComplete}
          />

          <EditCompany
            visible={showEditModal != null}
            onCancel={() => setShowEditModal(null)}
            onComplete={() => setShowEditModal(null)}
            enterpriseId={showEditModal}
          />
        </Col>
        <Col span="6" className="mb-4">
          <Form>
            <Form.Item>
              <Input.Search
                allowClear
                placeholder={translate("Buscar")}
                onSearch={searchData}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        rowKey={(i) => i.id}
        loading={
          loading
            ? { indicator: antIcon, className: "text-custom_blue" }
            : false
        }
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default CompaniesTable;
